import type { History } from 'history';

export const CLOSE_POLL_MS = 500;
// The popup posts its result and self-closes in the same turn, so wait this long before treating a close as empty.
const CLOSE_GRACE_MS = 400;

// Random: every widget frame has its own copy of this module, so a counter would repeat across two
// widgets on one page, and a repeated name lets the second widget navigate the first one's popup.
const popupId = () =>
  Array.from({ length: 2 }, () =>
    Math.floor(Math.random() * 2 ** 32)
      .toString(16)
      .padStart(8, '0'),
  ).join('');

// Safari only allows `window.open` inside the gesture: build the URL first, and await nothing before this.
export function openPopup({
  url,
  namePrefix,
  features,
}: {
  url: string;
  namePrefix: string;
  features: string;
}): Window | null {
  return window.open(url, `${namePrefix}${popupId()}`, features);
}

export function watchPopup(win: Window, { onClosedWithoutResult }: { onClosedWithoutResult: () => void }): () => void {
  let grace: number | null = null;

  const poll = window.setInterval(() => {
    if (!win.closed) return;
    window.clearInterval(poll);
    grace = window.setTimeout(() => {
      grace = null;
      onClosedWithoutResult();
    }, CLOSE_GRACE_MS);
  }, CLOSE_POLL_MS);

  return () => {
    window.clearInterval(poll);
    if (grace !== null) {
      window.clearTimeout(grace);
      grace = null;
    }
  };
}

export function hasLiveOpener(): boolean {
  const opener: Window | null = window.opener;
  return !!opener && !opener.closed;
}

export function postToOpener<T>(message: T): boolean {
  if (!hasLiveOpener()) return false;
  window.opener.postMessage(message, window.location.origin);
  return true;
}

export function parseFromPopup<T>(
  e: MessageEvent,
  expectedSource: Window | null,
  isKnown: (data: unknown) => data is T,
): T | null {
  if (!expectedSource || e.source !== expectedSource) return null;
  if (e.origin !== window.location.origin) return null;
  return isKnown(e.data) ? e.data : null;
}

// The router reverts a blocked pop itself, so nothing is pushed onto or popped off the history the frame
// shares with the host page; its listeners only see the unchanged location again (listenForBackNavigation).
// It can only revert to entries this document created: when the frame was loaded straight onto the checkout
// (a reload), the pop isn't reverted, and the frame's URL moves back while the router stays on the checkout.
// Only pops are held: in-app navigations, like the route to the confirmation, still go through.
export function holdHistory(history: History, popup: Window): () => void {
  return history.block((_location, action) => {
    if (action !== 'POP') return;
    popup.focus();
    return false;
  });
}

export type PopupHandle = {
  win: Window;
  /** Arms the close watch once; a second call, or a call after `end()`, arms nothing. */
  watch: (onClosedWithoutResult: () => void) => void;
  /** Stops the watch and releases the back hold; a no-op when called again. */
  end: () => void;
};

/**
 * One popup's lifecycle: opens it (first, inside the gesture), holds the history for as
 * long as it lives, and lets the caller arm the close watch with a callback that knows
 * what a close means — login aborts, payment tells a closed waiting page from a closed
 * pay page by its phase. `null` when blocked, with nothing armed.
 */
export function startPopup({
  url,
  namePrefix,
  features,
  history,
}: {
  url: string;
  namePrefix: string;
  features: string;
  history: History;
}): PopupHandle | null {
  const win = openPopup({ url, namePrefix, features });
  if (!win) return null;

  const release = holdHistory(history, win);
  let stopWatch: (() => void) | null = null;
  let ended = false;

  return {
    win,
    watch(onClosedWithoutResult) {
      if (ended || stopWatch) return;
      stopWatch = watchPopup(win, { onClosedWithoutResult });
    },
    end() {
      if (ended) return;
      ended = true;
      stopWatch?.();
      stopWatch = null;
      release();
    },
  };
}
